import { Boosts, type BoostView } from "@highmarch/app";
import type { BoostData } from "@highmarch/domain";
import { priceButtonHtml, secHtml, slotTileHtml, timerChipHtml } from "../components";
import { fmtCompact } from "../kit";
import { toast } from "../toast";
import { uiUrl } from "../portraits";
import { econCue, purchasePop, tickPill } from "../hub/purchaseFx";
import { gemIcon } from "../hub/econArt";
import { openItemPopup } from "../shops/itemPopup";
import { itemCardHtml } from "../shops/itemCard";
import { refusalWords } from "../hub/refusalWords";
import type { ShopDeps } from "../hub/shopSheet";
import { notEnoughGems } from "../hub/getGold";

/**
 * Battle boosts in the Shop's Supplies tab (owner 2026-10-21, docs/SHOP.md §23): one tile per boost — its potion on the
 * well with "1h" on the cap, "+20% damage", the time it has left while it runs (a live countdown), its gem price.
 * A buy confirms, spends through @highmarch/app Boosts (hub only) and redraws the shelf in place.
 */

export const boostIconUrl = (b: BoostData): string => uiUrl(`icons/${b.icon}.png`);
/** "+20% damage", "+30% fortress health". */
export const boostLine = (b: BoostData): string => `+${b.percent}% ${b.name.toLowerCase()}`;
/** "1h", "45m", "1h 20m" (whole minutes; the countdown chip rounds the same way). */
export function fmtBoostLeft(seconds: number): string {
  const m = Math.ceil(seconds / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60), r = m % 60;
  return r === 0 ? `${h}h` : `${h}h ${r}m`;
}

function tileHtml(v: BoostView, gems: number, now: number): string {
  const b = v.boost;
  const price = priceButtonHtml({ currency: "gems", amount: fmtCompact(b.gems), short: gems < b.gems, className: "ebuy b-boost", data: `data-boost="${b.id}"` });
  const left = v.left > 0 ? timerChipHtml(fmtBoostLeft(v.left), { inline: true, endsAt: now + v.left }) : "";
  return slotTileHtml({
    well: { art: boostIconUrl(b), cap: fmtBoostLeft(b.minutes * 60) }, name: boostLine(b), left, price,
    className: `pack boost${v.left > 0 ? " running" : ""}`, data: `data-boost="${b.id}"`,
  });
}

export function boostsShelfHtml(s: Boosts, gems: number, now: number): string {
  if (!s.shopOpen) return "";
  const tiles = s.view(now).map((v) => tileHtml(v, gems, now)).join("");
  return `<section class="ssec packs boosts">${secHtml("Boosts")}<div class="k-grid3 pgrid boostgrid">${tiles}</div></section>`;
}

/** Draws the shelf at the end of `container` and binds it; a buy redraws it in place. */
export function mountBoostsShelf(container: HTMLElement, d: ShopDeps): void {
  const s = new Boosts(d.catalog);
  const slot = document.createElement("div");
  slot.className = "boostslot";
  container.append(slot);
  const draw = (): void => {
    slot.innerHTML = boostsShelfHtml(s, d.catalog.economy.wallet.gems, d.now());
    // the whole tile opens the item popup (its price button's tap bubbles to the tile)
    for (const t of slot.querySelectorAll<HTMLElement>(".boost[data-boost]")) t.onclick = () => void buy(t.dataset.boost!, t);
  };
  const buy = async (id: string, card: Element | null): Promise<void> => {
    const b = s.data.items.find((x) => x.id === id);
    if (!b) return;
    const parent = d.hub.parent, wallet = d.catalog.economy.wallet;
    const running = s.view(d.now()).find((v) => v.boost.id === id)?.left ?? 0;
    const ok = await openItemPopup(parent, { title: "BOOST", price: `${gemIcon(20)} ${fmtCompact(b.gems)}`, className: "c-gems",
      body: itemCardHtml({ well: { art: boostIconUrl(b), cap: fmtBoostLeft(b.minutes * 60) }, name: boostLine(b), rarity: null,
        what: `In every battle you start for the next ${fmtBoostLeft(b.minutes * 60)}${running > 0 ? `, on top of the ${fmtBoostLeft(running)} left` : ""}.` }) });
    if (!ok) return;
    if (wallet.gems < b.gems) { void notEnoughGems(parent, b.gems - wallet.gems, slot.closest(".sheet")); return; }
    const before = wallet.gems;
    const r = s.buy(id, d.now(), "hub");
    if (r !== true) { toast(parent, r === "boost full" ? "That boost is full for the next day" : refusalWords(r)); draw(); return; }
    econCue("buy");
    purchasePop(card);
    d.hub.changed();
    tickPill("gems", before, wallet.gems);
    toast(parent, `${boostLine(b)} for your battles`);
    draw();
  };
  draw();
}
